'use client';

import Link from '@eliadmin/core/src/components/link';

import type { Reference } from '@eliadmin/core/src/api/references/references-types';

import { CarouselProvider } from '@eliadmin/core/src/components/ui/carousel-context';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { HeadingSubtitle, HeadingTitle } from '../shared/heading';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { Button } from '../ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';
import { Carousel } from '../ui/carousel';
import { ReferenceCard } from '../reference/reference-card';
import { Reveal } from '../ui/reveal';

interface ReferencesSectionProps {
  references: Reference[];
  title?: string;
  className?: {
    section?: string;
    container?: string;
    title?: string;
  };
}

export function References({ references, title, className }: ReferencesSectionProps) {
  const { t } = useTranslations();

  return (
    <CarouselProvider>
      <section
        className={cn(
          'custom-section',
          'w-full !pr-0 pb-6 pt-10 sm:pb-10 sm:pt-20 lg:pb-12 lg:pt-24',
          className?.section,
        )}
      >
        <div
          className={cn(
            'custom-container',
            'flex flex-col items-start justify-start gap-6 sm:gap-8 md:flex-row md:justify-between lg:gap-12',
            className?.container,
          )}
        >
          <div className="flex h-full w-full flex-col items-start justify-between md:h-[460px] md:min-w-[320px] md:max-w-[380px] lg:h-[500px]">
            <HeadingSubtitle subtitle={t('text1091')} />

            <div className="flex w-full flex-col items-start justify-start gap-6">
              <HeadingTitle
                title={title ?? t('text2322')}
                className={cn('mt-2 pr-4 sm:mt-0', className?.title)}
              />
              <Button asChild className="hidden whitespace-nowrap md:flex">
                <Link prefetchOnHover href={ROUTES.REFERENCES()}>
                  {t('text1247')}
                </Link>
              </Button>
            </div>
            <CarouselControls
              className={{
                main: 'hidden items-start justify-start gap-4 md:flex',
                button: 'border-none bg-[#fafafa]',
              }}
            />
          </div>
          <div className="w-full md:max-w-[800px]">
            <Carousel
              mobileProgressBar
              className={{
                wrapper: 'w-full',
                main: 'w-full pb-10 sm:rounded-[24px] sm:pb-0',
                container: 'w-full gap-4 sm:gap-5 lg:gap-6',
                slide:
                  'relative aspect-square w-full max-w-[310px] overflow-hidden rounded-[16px] sm:max-w-[400px] sm:rounded-[24px] md:max-w-[460px] lg:max-w-[500px]',
              }}
              slides={references.map(reference => (
                <Reveal key={reference.id} className="w-full">
                  <ReferenceCard reference={reference} deferImage />
                </Reveal>
              ))}
            />
          </div>
          <div className="block w-full pr-6 sm:hidden">
            <Button asChild className="whitespace-nowrap">
              <Link prefetchOnHover href={ROUTES.REFERENCES()}>
                {t('text1247')}
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </CarouselProvider>
  );
}
